import { FAMILY_LABELS } from '../utils';

/**
 * Radar comparatif SVG — 4 axes alignés avec les 4 familles validées par Yovan
 * (mapping KPI_FAMILY_MAP). Deux polygones superposés :
 *   - Benchmark : pointillés brun doré, baseline 50/100
 *   - Officine  : bordeaux semi-transparent, scores réels (0-100)
 *
 * Les couleurs sont en hex direct car SVG `fill` / `stroke` n'acceptent pas
 * les classes Tailwind. Mapping aligné sur la palette `cabinet-*`.
 */

const AXES = [
  { key: 'activite', label: FAMILY_LABELS.activite },
  { key: 'rentabilite', label: FAMILY_LABELS.rentabilite },
  { key: 'stock', label: FAMILY_LABELS.stock },
  { key: 'patientele', label: FAMILY_LABELS.patientele },
];

const BENCHMARK_BASELINE = 50;
const BORDEAUX = '#8B0028';
const GOLD = '#B5862A';
const BORDER = '#E8DCC4';
const INK = '#1A1817';
const CREAM = '#FBF8F3';

export default function ComparativeRadar({ subscores = {}, size = 260 }) {
  const center = size / 2;
  const radius = size * 0.34;
  const N = AXES.length;

  const points = AXES.map((axis, i) => {
    const angle = -Math.PI / 2 + (i / N) * 2 * Math.PI;
    const score = subscores[axis.key];
    const value = score != null ? Math.max(0, Math.min(100, score)) : 0;
    const labelDist = radius + 22;
    return {
      ...axis,
      x: center + radius * Math.cos(angle) * (value / 100),
      y: center + radius * Math.sin(angle) * (value / 100),
      bx: center + radius * Math.cos(angle) * (BENCHMARK_BASELINE / 100),
      by: center + radius * Math.sin(angle) * (BENCHMARK_BASELINE / 100),
      lx: center + labelDist * Math.cos(angle),
      ly: center + labelDist * Math.sin(angle),
      ex: center + radius * Math.cos(angle),
      ey: center + radius * Math.sin(angle),
    };
  });

  const yourPoly = points.map((p) => `${p.x},${p.y}`).join(' ');
  const benchPoly = points.map((p) => `${p.bx},${p.by}`).join(' ');

  return (
    <div className="flex flex-col items-center" data-testid="comparative-radar">
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        role="img"
        aria-label="Radar comparatif des 4 familles vs benchmark national"
      >
        {[0.25, 0.5, 0.75, 1].map((r) => (
          <circle key={r} cx={center} cy={center} r={radius * r} fill="none" stroke={BORDER} strokeWidth="1" />
        ))}
        {points.map((p, i) => (
          <line key={i} x1={center} y1={center} x2={p.ex} y2={p.ey} stroke={BORDER} strokeWidth="1" />
        ))}
        <polygon points={benchPoly} fill="rgba(181, 134, 42, 0.10)" stroke={GOLD} strokeWidth="1.5" strokeDasharray="4,3" />
        <polygon points={yourPoly} fill="rgba(139, 0, 40, 0.18)" stroke={BORDEAUX} strokeWidth="2" />
        {points.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r="4" fill={BORDEAUX} stroke={CREAM} strokeWidth="2" />
        ))}
        {points.map((p, i) => (
          <text
            key={i} x={p.lx} y={p.ly}
            textAnchor="middle" dominantBaseline="middle"
            fontFamily="Manrope, sans-serif"
            fontSize="10" fontWeight="600" fill={INK}
          >
            {p.label}
          </text>
        ))}
      </svg>
      <div className="mt-4 flex items-center gap-4 text-[10px] text-cabinet-ink-secondary uppercase tracking-[0.18em]">
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-3 h-2" style={{ background: BORDEAUX }} aria-hidden /> Vous
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-3 h-px border-t border-dashed" style={{ borderColor: GOLD }} aria-hidden /> Benchmark
        </span>
      </div>
    </div>
  );
}
